/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Drew Burnett
 */

import { 
    useGetDehydratedUserQuery,
    ProfileDocument,
    CollectionDocument,
    useFindProfileQuery,
    FindProfileDocument,
    useCollectionLazyQuery, 
    useAddCollectionMutation,
    useAddCollectionItemMutation,
    GetMomentCollectionInfoDocument
} from "../../generated/graphql"
import { traceIdPlusUrl } from "../../util/links"
import "./AutoAddMomentToTraceiDWrapper.css"
import { AutoAddMomentToTraceiD } from "trux"
import React, { useEffect, useState, useRef } from "react"

// Auto Add Moment To TraceiD Wrapper
const AutoAddMomentToTraceiDWrapper = ({ token, title, team, moment, forceAutoAdd, disableAutoAdd, onClose }) => {

    // The token ya'll
    const tokenReady = token?.user_id

    // Some vars we'll need
    const lastRunId = useRef('')
    const [playlistTitle, setPlaylistTitle] = useState("Favorites playlist")
    const [didCreateNewItem, setDidCreateNewItem] = useState(false)
    const [playlistLink, setPlaylistLink] = useState(null)
    const [thumbnail, setThumbnail] = useState(null)
    const [success, setSuccess] = useState(null)
    const [loading, setLoading] = useState(true)

    // Hey all!

    // Get the user
    const { 
        data: userData, 
        loading: userLoading, 
        error: userError 
    } = useGetDehydratedUserQuery({
          variables: {
             user_id: token?.user_id,
             token: token
          },
          skip: !tokenReady
    });
    let user = userData ? userData.user : null 
    
    // Get the user's trace id profile
    const { 
        data: profileData, 
        loading: profileLoading, 
        error: profileError 
    } = useFindProfileQuery({
          variables: {
             user_id: token?.user_id,
             token: token
          },
          skip: !tokenReady
    });
    const profile = profileData ? profileData.profile : null

    // Is anyone reading these comments?

    // Add item to collection mutation
    const [
        addCollectionItem, 
        { 
            data: addCollectionItemData, 
            loading: addCollectionItemLoading, 
            error: addCollectionItemError 
        }
    ] = useAddCollectionItemMutation({
        variables: {
            token: token,
        },
        skip: !tokenReady,
        refetchQueries: [
            FindProfileDocument, "findProfile",
            CollectionDocument, "collection",
            ProfileDocument, "profile",
            GetMomentCollectionInfoDocument, "getMomentCollectionInfo"
        ],
    })

    const [fetchCollection] = useCollectionLazyQuery({
        variables: {
            token: token,
        }
    })

    // If you are reading these comments, I hope you're having a great day!

    // Add collection mutation
    const [
        addCollectionMutation, 
        { 
            data: addCollectionData, 
            loading: addCollectionLoading, 
            error: addCollectionError
        },
    ] = useAddCollectionMutation({
        variables: {
            token: token,
        },
        skip: !tokenReady,
        refetchQueries: [FindProfileDocument, "findProfile"],
    })

    // Throw me a shoutout in the PR if you enjoyed reading these comments :)

    const getFavoritesCollectionHashKey = () => {
        let collections = profile?.collections
        let favoritesCollection = collections.find(collection => collection.title === "Favorites")
        let favoritesCollectionHashKey = favoritesCollection ? favoritesCollection.hash_key : null
        return favoritesCollectionHashKey 
    }

    const getExistingMoment = async (collectionHashKey) => {
        let collection = await fetchCollection({
            variables: {
                token: token,
                hash_key: collectionHashKey
            }
        })
        let found = collection?.data?.collection?.items.find(item => {
            return parseInt(item.moment.game_clip_id) === parseInt(moment.game_clip_id)
        })
        return found?.moment?.game_clip_id
    }

    const addMomentToFavorites = async () => {
        if (disableAutoAdd || success) {
            setDidCreateNewItem(false)
            setLoading(false)
            setSuccess(true)
        }
        let favoritesCollectionHashKey = getFavoritesCollectionHashKey()
        if (!favoritesCollectionHashKey) {
            let res = await addCollectionMutation({
                variables: {
                    edit: {
                        title: "Favorites",
                        public: false,
                        color: 1
                    },
                    user_playlist_id: profile.user_playlist_id
                }
            })
            favoritesCollectionHashKey = res?.data?.addCollection?.hash_key
        }
        if (favoritesCollectionHashKey) {
            // First check whether the item already exists in the collection
            let existingClipId = await getExistingMoment(favoritesCollectionHashKey)
            if (!existingClipId || forceAutoAdd) {
                let addCollectionItemRes = await addCollectionItem({
                    variables: {
                        hash_key: favoritesCollectionHashKey,
                        add: {
                            user_playlist_clip_id: moment.game_clip_id
                        }
                    }
                })
                let addCollectionItemData = addCollectionItemRes?.data?.addCollectionItem
                if (addCollectionItemData?.success) {
                    setSuccess(true)
                    // Set the playlist link now that we have the clip
                    let theLink = traceIdPlusUrl(profile.hash_key, favoritesCollectionHashKey, addCollectionItemData.user_playlist_clip_id)
                    setPlaylistLink(theLink)
                    setDidCreateNewItem(true)
                    setLoading(false)
                } else {
                    setDidCreateNewItem(false)
                    setLoading(false)
                    setSuccess(false)
                }
            } else {
                let theLink = traceIdPlusUrl(profile.hash_key, favoritesCollectionHashKey, existingClipId)
                setPlaylistLink(theLink)
                setDidCreateNewItem(false)
                setLoading(false)
                setSuccess(true)
            }
        } else {
            // Error
            setLoading(false)
            setSuccess(false)
        }
    }

    useEffect(() => {
        if (moment) {
            let videoPart = encodeURI(`api/teams/${team.name}/games/${team.name}-${moment.game_id}/${moment.dynamic?.hls}`)
            let thumbnailUrl = `https://lapi.traceup.com/exec-${process.env.REACT_APP_TRACE_ENV}/clip/thumbnail?video=${videoPart}`
            setThumbnail(thumbnailUrl)
        }
    }, [moment])

    useEffect(() => {
        if (profile && moment && !disableAutoAdd && lastRunId.current !== `${profile.hash_key}-${moment.game_clip_id}`) {
            lastRunId.current = `${profile.hash_key}-${moment.game_clip_id}`
            addMomentToFavorites()
        }
    }, [profile, moment])

    return (
        <AutoAddMomentToTraceiD
            user={user}
            title={title}
            moment={moment}
            success={success}
            loading={loading}
            thumbnail={thumbnail}
            playlistLink={playlistLink}
            playlistTitle={playlistTitle}
            didCreateNewItem={didCreateNewItem}
            onClose={onClose}
        />
    )
}

AutoAddMomentToTraceiDWrapper.defaultProps = {
    forceAutoAdd: false, 
    disableAutoAdd: false,
}

export { AutoAddMomentToTraceiDWrapper }